import Container from '@amzn/awsui-components-react/polaris/container';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  AmbassadorRegistrationForm,
  AmbassadorRegistrationFormValues,
} from '../components/ambassadors/ambassadorRegistrationForm';
import { featureIsEnabled, useFeatures } from '../hooks/features';
import { COMMUNITY_AMBASSADOR_FEATURE_FLAG } from '../utils/constants';
import { useState } from 'react';
import { useCreateAmbassador } from '../hooks/ambassadors/ambassadorCreation/createAmbassadorMutation';
import { IneligibilityReasons } from '@amzn/red-velvet-api';
import {
  AmbassadorRegistrationFailure,
  AmbassadorRegistrationSuccess,
} from '../components/ambassadors/ambassadorRegistrationResults';
import { getAuthAlias } from '../utils/auth';
import { ambassadorFormValuesToRequest } from '../utils/ambassadors';
import { Alert, AppLayout, HelpPanel, Link, Spinner } from '@amzn/awsui-components-react';
import { AmbassadorRegistrationHelp } from '../components/ambassadors/ambassadorRegistrationHelp';
import { publishKatalMetric } from '../components/katalAnalytics';
import { useUser } from '../hooks/user';
import { useGetAmbassador } from '../hooks/ambassadors/getAmbassador';

export function AmbassadorRegistration() {
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [successfulRegistration, setSuccessfulRegistration] = useState(false);
  const [ineligibilityReasons, setIneligibilityReasons] = useState<
    IneligibilityReasons | undefined
  >();

  const [formValues, setFormValues] = useState<AmbassadorRegistrationFormValues>({
    country: null,
    city: null,
    siteCode: null,
    businessUnit: null,
    siteRepresentation: false,
    teamRepresentation: false,
    employeeLedGroupRepresentation: false,
    howDidYouHearSpecification: '',
    howDidYouHear: null,
    myManagerHasApprovedThisRole: false,
    teamLocation: [],
    ergLocation: [],
    representedSiteCodes: [],
    ergName: null,
    representMultipleSites: false,
    sendMeACopy: false,
  });

  const { t } = useTranslation(['ambassadors', 'translation']);
  const createAmbassador = useCreateAmbassador();
  const user = useUser();
  const {
    data: ambassadorData,
    isLoading: isLoadingAmbassador,
    isError,
    error: ambassadorError,
  } = useGetAmbassador(user.employeeId || '');
  const isNotFound =
    (ambassadorError as Error & { $metadata?: { httpStatusCode?: number } })?.$metadata
      ?.httpStatusCode === 404;
  const hasData = ambassadorData !== undefined;
  const isAlreadyRegistered = ambassadorData?.ambassador?.isRegistered === true;

  const getActiveUserAlias = (): string | undefined => {
    return getAuthAlias() ?? undefined;
  };

  const submitRegistration = async () => {
    const registerRequest = ambassadorFormValuesToRequest(formValues, getActiveUserAlias(), t);
    createAmbassador.mutate(registerRequest, {
      onSuccess: (data) => {
        publishKatalMetric('update_ambassador_success', JSON.stringify(registerRequest));
        setHasSubmitted(true);
        setSuccessfulRegistration(data.result.isSuccess);
        if (data.result.ineligibilityReasons) {
          setIneligibilityReasons(data.result.ineligibilityReasons);
        }
      },
      onError: (err) => {
        publishKatalMetric(
          'update_ambassador_error',
          JSON.stringify({ ...registerRequest, error: err?.message })
        );
        console.error(err);
      },
    });
  };

  if (featureIsEnabled(COMMUNITY_AMBASSADOR_FEATURE_FLAG, features, searchParams)) {
    return (
      <AppLayout
        navigationHide
        toolsOpen={true}
        tools={
          <HelpPanel header={<h2>{t('ambassadorRegistration.help.title')}</h2>}>
            {<AmbassadorRegistrationHelp />}
          </HelpPanel>
        }
        content={
          <Container fitHeight variant="stacked">
            {!hasSubmitted ? (
              <>
                <h1 data-testid="ambassador-registration-header">
                  {t('ambassadorRegistration.title')}
                </h1>
                {isLoadingAmbassador || (!hasData && !isError) ? (
                  <Spinner />
                ) : isError && !isNotFound ? (
                  <Alert type="error">{t('ambassadorRegistration.loadFailed')}</Alert>
                ) : isAlreadyRegistered ? (
                  <Alert type="info" header={t('ambassadorRegistration.alreadyRegistered.header')}>
                    {t('ambassadorRegistration.alreadyRegistered.message')}{' '}
                    <Link href="/ambassadors">
                      {t('ambassadorRegistration.alreadyRegistered.viewDirectory')}
                    </Link>
                  </Alert>
                ) : (
                  <AmbassadorRegistrationForm
                    formValues={formValues}
                    setFormValues={setFormValues}
                    onSubmit={submitRegistration}
                    loading={createAmbassador.isPending}
                  />
                )}
              </>
            ) : (
              <>
                {successfulRegistration ? (
                  <AmbassadorRegistrationSuccess />
                ) : (
                  <AmbassadorRegistrationFailure ineligibilityReasons={ineligibilityReasons} />
                )}
              </>
            )}
          </Container>
        }
      />
    );
  }
  return <></>;
}
